// src/modules/Roles/components/modals/CreateModal.tsx
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox } from '@shared/components/ui/checkbox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@shared/components/ui/shadcn/accordion';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TENANT_3PL_GROUP_MAP } from '../../config/permissionGroups';
import { usePermissionGroups, usePermissionSelection, usePermissionsList } from '../../hooks';

export interface CreateModalRef {
  open: (options: {
    mode: 'create' | 'edit';
    initialName?: string;
    initialPermissions?: string[];
    onSubmit: (name: string, permissions: string[]) => Promise<void>;
  }) => void;
}

export const CreateModal = forwardRef<CreateModalRef>((_, ref) => {
  const { t } = useTranslation('rolesModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const [roleName, setRoleName] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [searchPermissionTerm, setSearchPermissionTerm] = useState('');
  const onSubmitRef = useRef<((name: string, permissions: string[]) => Promise<void>) | null>(null);

  // 🎯 Obtener permisos desde la API
  const {
    permissions: allPermissions,
    isLoading: isLoadingPermissions,
    error: permissionsError,
    refetch: refetchPermissions,
  } = usePermissionsList();

  // 🎯 Filtrar permisos basado en la búsqueda local
  const filteredPermissions = useMemo(() => {
    // Validar que allPermissions sea un array válido con permisos válidos
    if (!Array.isArray(allPermissions) || allPermissions.length === 0) {
      return [];
    }

    // Filtrar solo permisos inválidos (sin slug)
    // Los permisos .view ahora SÍ aparecen en el listado
    const validPermissions = allPermissions.filter((perm) => perm && perm.slug && typeof perm.slug === 'string');

    if (validPermissions.length === 0) {
      return [];
    }

    if (!searchPermissionTerm.trim()) {
      return validPermissions;
    }

    const searchLower = searchPermissionTerm.toLowerCase().trim();
    return validPermissions.filter((perm) => {
      try {
        const translatedName = t('permissions.' + perm.slug);
        return translatedName.toLowerCase().includes(searchLower);
      } catch {
        // Si hay error en la traducción, usar el slug directamente
        return perm.slug.toLowerCase().includes(searchLower);
      }
    });
  }, [allPermissions, searchPermissionTerm, t]);

  // 🎯 Agrupar permisos filtrados por ruta usando configuración de tenant_3pl
  const groupedPermissions = usePermissionGroups(filteredPermissions, TENANT_3PL_GROUP_MAP);

  // 🎯 Hook para manejar la selección de permisos con lógica de .view automático
  const {
    handlePermissionChange,
    handleSelectAll,
    handleGroupSelectAll,
    isGroupFullySelected,
    isGroupPartiallySelected,
  } = usePermissionSelection({
    allPermissions: allPermissions || [],
    selectedPermissions,
    setSelectedPermissions,
    customGroupMap: TENANT_3PL_GROUP_MAP,
  });

  // 🎯 Verificar si todos los permisos filtrados están seleccionados
  const allSelected = useMemo(() => {
    if (filteredPermissions.length === 0) return false;
    // Solo verificar los permisos no-.view para el "seleccionar todos"
    const nonViewPermissions = filteredPermissions.filter((p) => !p.slug.endsWith('.view'));
    if (nonViewPermissions.length === 0) return false;
    return nonViewPermissions.every((perm) => selectedPermissions.includes(perm.slug));
  }, [filteredPermissions, selectedPermissions]);

  // 🎯 Sincronizar permisos seleccionados cuando se cargan desde la API
  const initialPermissionsRef = useRef<string[] | undefined>(undefined);

  useImperativeHandle(ref, () => ({
    open: ({ mode, initialName, initialPermissions, onSubmit }) => {
      setMode(mode);
      setRoleName(initialName || '');
      setSearchPermissionTerm(''); // 🎯 Limpiar búsqueda al abrir el modal

      // Guardar permisos iniciales para sincronizar cuando se carguen desde la API
      if (initialPermissions) {
        initialPermissionsRef.current = initialPermissions;
        // Si ya tenemos los permisos cargados, aplicarlos inmediatamente
        if (allPermissions && allPermissions.length > 0) {
          setSelectedPermissions(allPermissions.filter((p) => initialPermissions.includes(p.slug)).map((p) => p.slug));
          initialPermissionsRef.current = undefined;
        }
      } else {
        setSelectedPermissions([]);
        initialPermissionsRef.current = undefined;
      }

      onSubmitRef.current = onSubmit;
      modalRef.current?.openModal();
    },
  }));

  useEffect(() => {
    if (allPermissions && allPermissions.length > 0 && initialPermissionsRef.current) {
      setSelectedPermissions(
        allPermissions.filter((p) => initialPermissionsRef.current?.includes(p.slug)).map((p) => p.slug),
      );
      initialPermissionsRef.current = undefined; // Limpiar después de aplicar
    }
  }, [allPermissions]);

  const handleConfirm = async () => {
    if (!roleName || selectedPermissions.length === 0 || !allPermissions) return;
    // selectedPermissions ya contiene los slugs directamente
    if (onSubmitRef.current) {
      await onSubmitRef.current(roleName, selectedPermissions);
    }
    modalRef.current?.closeModal();
  };

  return (
    <ModalCenter
      ref={modalRef}
      title={mode === 'edit' ? t('CreateModal.editTitle', 'Editar rol') : t('CreateModal.title')}
      type="default"
      width="lg"
      classNameBody="h-[calc(100vh-120px)] max-h-[calc(100vh-120px)] overflow-hidden flex flex-col"
    >
      <div className="flex flex-col h-full">
        <div className="space-y-4 flex-1 overflow-y-auto min-h-0">
          <div className="mt-2">
            <input
              type="text"
              className="w-full bg-transparent border border-card-border rounded px-3 py-2 text-sm outline-none focus:border-primary"
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              placeholder={t('CreateModal.roleName')}
            />
          </div>
          <div>
            {/* 🎯 Campo de búsqueda de permisos */}
            <div className="relative mb-4 mt-4">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-icons w-3.5 h-3.5 z-10"
              />
              <Input
                type="text"
                className="w-full pl-9 !bg-transparent"
                value={searchPermissionTerm}
                onChange={(e) => setSearchPermissionTerm(e.target.value)}
                placeholder={t('CreateModal.searchPermissions', 'Buscar permisos')}
                data-testid="permission-search-input"
              />
            </div>

            {/* 🎯 Checkbox "Seleccionar todos" - Solo muestra los permisos filtrados */}
            <label className="flex gap-3 items-center mb-4">
              <Checkbox
                checked={allSelected && filteredPermissions.length > 0}
                onCheckedChange={() => handleSelectAll(filteredPermissions)}
                disabled={filteredPermissions.length === 0}
              ></Checkbox>
              <span className="font-medium text-sm">
                {t('CreateModal.selectAll', {
                  count: filteredPermissions.length || allPermissions.length,
                })}
              </span>
            </label>

            {/* 🎯 Estados de carga y error */}
            {isLoadingPermissions && (
              <div className="border border-card-border rounded-lg p-8 text-center">
                <div className="text-sm text-complementary-text">
                  {t('CreateModal.loadingPermissions', 'Cargando permisos...')}
                </div>
              </div>
            )}

            {permissionsError && (
              <div className="border border-card-border rounded-lg p-4 text-center">
                <div className="text-sm text-destructive">
                  {t('CreateModal.errorLoadingPermissions', 'Error al cargar los permisos')}
                </div>
                <Button variant="outline" size="sm" onClick={() => refetchPermissions()} className="mt-2">
                  {t('CreateModal.retry', 'Reintentar')}
                </Button>
              </div>
            )}

            {/* 🎯 Lista de permisos agrupados por ruta */}
            {!isLoadingPermissions && !permissionsError && (
              <div className="border border-card-border rounded-lg">
                {groupedPermissions.length > 0 ? (
                  <Accordion type="multiple" className="w-full">
                    {groupedPermissions.map((group) => {
                      const groupKey = group.group;
                      const isFullySelected = isGroupFullySelected(group.permissions);
                      const isPartiallySelected = isGroupPartiallySelected(group.permissions);
                      // Contar TODOS los permisos del grupo, incluyendo .view
                      const selectedCount = group.permissions.filter((p) =>
                        selectedPermissions.includes(p.slug),
                      ).length;

                      return (
                        <AccordionItem
                          key={groupKey}
                          value={groupKey}
                          className="border-b border-card-border last:border-b-0"
                        >
                          <AccordionTrigger className="px-4 py-3 hover:no-underline">
                            <div className="flex items-center gap-3 flex-1">
                              <Checkbox
                                checked={isFullySelected ? true : isPartiallySelected ? 'indeterminate' : false}
                                onCheckedChange={() => handleGroupSelectAll(group.permissions)}
                                onClick={(e) => e.stopPropagation()}
                                className="mr-2"
                              />
                              <span className="font-medium text-sm">{t(`permissionGroups.${groupKey}`, groupKey)}</span>
                              <span className="text-xs text-complementary-text ml-auto">
                                ({selectedCount}/{group.permissions.length})
                              </span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-3">
                            <div className="space-y-2 pt-2">
                              {group.permissions.map((perm) => {
                                const isChecked = selectedPermissions.includes(perm.slug);

                                return (
                                  <div
                                    key={perm.slug}
                                    className="flex items-center px-2 py-2 hover:bg-accent/50 rounded transition-colors"
                                  >
                                    <Checkbox
                                      checked={isChecked}
                                      onCheckedChange={() => handlePermissionChange(perm.slug)}
                                      className="mr-3"
                                    />
                                    <span className="text-sm">{t('permissions.' + perm.slug)}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                ) : (
                  <div className="px-4 py-3 text-sm text-complementary-text text-center">
                    {t('CreateModal.noPermissionsFound', 'No se encontraron permisos')}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer en flujo normal: siempre al final, sin sticky para evitar tapar contenido */}
        <div className="flex justify-end gap-2 pt-4 bg-body-bg">
          <Button
            variant="outline"
            size="lg"
            onClick={() => modalRef.current?.closeModal()}
            data-testid="create-role-cancel-button"
          >
            {t('CreateModal.cancel')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleConfirm}
            disabled={!roleName || selectedPermissions.length === 0 || isLoadingPermissions || !allPermissions}
            data-testid="create-role-submit-button"
          >
            {mode === 'edit' ? t('CreateModal.save', 'Guardar') : t('CreateModal.create')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

CreateModal.displayName = 'CreateModal';
